<template>
    <b-modal
        :visible="visible"
        @hide="$emit('close')"
        title="Editar Seguimiento"
        size="md"
        centered
        no-close-on-backdrop
    >
        <div class="tracing-form" v-if="tracing">
            <b-row>
                <!-- Estado -->
                <b-col md="6">
                    <div class="form-group">
                        <label class="form-label required">Estado</label>
                        <el-select
                            v-model="form.status"
                            placeholder="Seleccione estado"
                            class="w-100"
                            size="small"
                        >
                            <el-option
                                v-for="status in statusOptions"
                                :key="status.value"
                                :label="status.label"
                                :value="status.value"
                            />
                        </el-select>
                    </div>
                </b-col>
                <!-- Fecha del Reporte (readonly) -->
                <b-col md="6">
                    <div class="form-group">
                        <label class="form-label">Fecha del reporte</label>
                        <el-date-picker
                            v-model="form.report_date"
                            type="date"
                            placeholder="Fecha del reporte"
                            format="dd/MM/yyyy"
                            value-format="yyyy-MM-dd"
                            size="small"
                            class="w-100"
                            disabled
                        />
                    </div>
                </b-col>
            </b-row>

            <!-- Condición Reportada -->
            <div class="form-group">
                <label class="form-label required">Condición reportada</label>
                <el-input
                    v-model="form.reported_condition"
                    type="textarea"
                    :rows="4"
                    placeholder="Describa la condición reportada..."
                    maxlength="2000"
                    show-word-limit
                />
            </div>

            <!-- Adjunto actual -->
            <div class="form-group" v-if="currentAttachment">
                <label class="form-label">Adjunto actual</label>
                <div class="current-attachment">
                    <i :class="getFileIcon(currentAttachment.original_name)" class="mr-2"></i>
                    <span>{{ currentAttachment.original_name }}</span>
                </div>
            </div>

            <!-- Nuevo Adjunto -->
            <div class="form-group">
                <label class="form-label">{{ currentAttachment ? 'Reemplazar adjunto' : 'Adjunto' }} (máx. 20 MB)</label>
                <div class="file-upload-wrapper">
                    <input 
                        type="file" 
                        ref="fileInput"
                        @change="handleFileChange"
                        class="file-input-hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    />
                    <div class="file-upload-box" @click="$refs.fileInput.click()">
                        <span v-if="!form.attachment" class="text-muted">
                            <i class="fa fa-paperclip mr-1"></i> Seleccione archivo
                        </span>
                        <span v-else class="text-primary">
                            <i class="fa fa-file mr-1"></i> {{ form.attachment.name }}
                            <i class="fa fa-times ml-2 text-danger" @click.stop="removeFile"></i>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <template #modal-footer>
            <b-button variant="secondary" @click="$emit('close')">
                Cancelar
            </b-button>
            <b-button 
                variant="primary" 
                @click="handleSubmit"
                :disabled="loading || !form.status || !form.reported_condition"
            >
                <i v-if="loading" class="fa fa-spinner fa-spin mr-1"></i>
                <i v-else class="fa fa-save mr-1"></i>
                Guardar Cambios
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    name: 'EditTracingModal',
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        tracing: {
            type: Object,
            default: null,
        },
        companyId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            form: {
                status: '',
                report_date: '',
                reported_condition: '',
                attachment: null,
            },
            statusOptions: [
                { value: 'Abierto', label: 'Abierto' },
                { value: 'Proceso', label: 'En Proceso' },
                { value: 'Cerrado por verificar', label: 'Cerrado por verificar' },
                { value: 'Cerrado', label: 'Cerrado' },
            ],
        };
    },
    computed: {
        currentAttachment() {
            if (this.tracing && this.tracing.attachments && this.tracing.attachments.length > 0) {
                return this.tracing.attachments[0];
            }
            return null;
        },
    },
    watch: {
        visible(newVal) {
            if (newVal && this.tracing) {
                this.form.status = this.tracing.status || 'Abierto';
                this.form.report_date = this.tracing.report_date;
                this.form.reported_condition = this.tracing.reported_condition || '';
                this.form.attachment = null;
            }
        },
    },
    methods: {
        ...mapActions('skudo/tracings', ['updateTracing']),

        handleFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                if (file.size > 20 * 1024 * 1024) {
                    this.$alertWarning('El archivo no debe superar 20 MB');
                    return;
                }
                this.form.attachment = file;
            }
        },

        removeFile() {
            this.form.attachment = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },

        getFileIcon(mimeType) {
            if (!mimeType) return 'fa fa-file';
            if (mimeType.includes('pdf')) return 'fa fa-file-pdf-o';
            if (mimeType.includes('word') || mimeType.includes('document')) return 'fa fa-file-word-o';
            if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'fa fa-file-excel-o';
            return 'fa fa-file-o';
        },

        async handleSubmit() {
            if (!this.form.status) {
                this.$alertWarning('Debe seleccionar un estado');
                return;
            }
            if (!this.form.reported_condition) {
                this.$alertWarning('Debe ingresar la condición reportada');
                return;
            }

            this.loading = true;
            try {
                const response = await this.updateTracing({
                    id: this.tracing.id,
                    payload: {
                        company_id: this.companyId,
                        status: this.form.status,
                        reported_condition: this.form.reported_condition,
                        attachment: this.form.attachment,
                    },
                });

                if (response.data) {
                    this.$alertSuccess(response.message || 'Seguimiento actualizado correctamente');
                    // El state ya se actualizó via mutation en el action
                    this.$emit('updated');
                } else {
                    this.$alertError(response.message || 'Error al actualizar el seguimiento');
                }
            } catch (error) {
                this.$alertError(error.message || 'Error al actualizar el seguimiento');
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.tracing-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #374151;
}

.form-label.required::after {
    content: ' *';
    color: #dc2626;
}

/* Current Attachment */
.current-attachment {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 0.85rem;
    color: #374151;
}

.current-attachment i {
    font-size: 1.2rem;
}

.current-attachment .fa-file-pdf-o {
    color: #dc2626;
}

.current-attachment .fa-file-word-o {
    color: #2563eb;
}

/* File Upload Styles */
.file-upload-wrapper {
    position: relative;
}

.file-input-hidden {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}

.file-upload-box {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    cursor: pointer;
    transition: border-color 0.2s;
    font-size: 0.85rem;
}

.file-upload-box:hover {
    border-color: #409eff;
}

.file-upload-box .fa-times {
    cursor: pointer;
}

.file-upload-box .fa-times:hover {
    color: #dc2626 !important;
}
</style>
